import {
	addUnit,
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDateFromQuery,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import {
	AsyncSelect,
	AsyncSelectProps,
	Badge,
	Box,
	Button,
	Container,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	RelativeTime,
	SelectInput,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	type TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import useSWR from "swr"
import { SelectField, SelectInputField } from "@sembark-travel/ui-form"
import { AIParsingStatus, ITrip } from "../Trips/store"
import { getParsingBadge, TParsingSnapshot } from "../MailMessages"
import { TWalletStats, WalletBalanceCard } from "../SubscriptionWalletCredits"
import { PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import { generatePath } from "../router-utils"

type TIntervalType = "day" | "week" | "month"

type TStatusFilter = AIParsingStatus | undefined

type TTenantOption = { id: number; name: string }

export type TUsageViewFilters = TSearchParams & {
	intervalType: TIntervalType
	after: Date
	before: Date
	status?: TStatusFilter
	tenant?: TTenantOption | null
}

type TFiltersInQuery = TSearchParams & {
	intervalType: TIntervalType
	after: string
	before: string
	status?: TStatusFilter
	tenant?: string
}

type TCreatedBy = {
	id: number
	name?: string
	email?: string
}

type TParsingItem = TParsingSnapshot & {
	status: "pending" | "completed" | "failed"
	credits_consumed?: string
	completed_at?: string
	created_at?: string
	created_by?: TCreatedBy
	trip?: ITrip
}

type TUsageStats = {
	data: {
		range: {
			after: string | null
			before: string | null
			interval_type: TIntervalType
		}
		total: number
		avg_credits_per_parse?: number | null
		completed?: number | null
		failed?: number | null
		wallet?: (TWalletStats & { approx_remaining_parses?: number | null }) | null
		credit_used?: number | null
		supplier_cost?: Array<{ currency: string; total: number }> | null
	}
}

function SelectTenants(props: Omit<AsyncSelectProps, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			cacheKey="package-parser-tenants"
			fetch={(q) =>
				xhr
					.get("/trip-quote-parsings/tenants", {
						params: { q },
					})
					.then((resp) => resp.data.data)
			}
			{...props}
		/>
	)
}

export function AIParsingUsageView() {
	const { user } = useAuthUser()
	const { hasPermission } = useCheckPermissions()
	const adminMode = !user?.tenant_id
	const canViewWallet = hasPermission(PERMISSIONS.MANAGE_TENANTS)
	const [query, setQuery] = useLocationQuery<
		TUsageViewFilters,
		TFiltersInQuery
	>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TUsageViewFilters>(query)

	const requestParams = useMemo(
		() => ({
			page: params.page || 1,
			q: params.q || undefined,
			after: dateToUTCString(params.after),
			before: dateToUTCString(params.before),
			status: params.status || undefined,
			interval_type: params.intervalType,
			tenant_id: adminMode && params.tenant ? params.tenant.id : undefined,
		}),
		[params, adminMode]
	)

	const statsParams = useMemo(() => {
		const { page: _page, ...rest } = requestParams
		return rest
	}, [requestParams])

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	const xhr = useXHR()
	const { data: stats } = useSWR<TUsageStats>(
		["quote-parsing-usage-stats", statsParams, adminMode ? "admin" : "tenant"],
		() =>
			xhr
				.get("/trip-quote-parsings/usage-stats", {
					params: statsParams,
				})
				.then((resp) => resp.data)
	)

	return (
		<Search
			title={"Trip Quote Parsing Usage"}
			initialParams={params}
			onSearch={setParams}
			placeholder="Search by id, guest name"
			Filters={Filters}
			resetParams={(p) => ({
				q: "",
				page: 1,
				after: p.after,
				before: p.before,
				intervalType: p.intervalType,
				status: undefined,
			})}
			areAdvancedFiltersApplied={(params) => {
				const { after, before, intervalType, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={({ setSearchParams, searchParams }) => (
				<Inline alignItems="center" gap="4">
					<Inline alignItems="center" gap="2">
						<Button
							onClick={() => {
								const after = startOf(
									subtractUnit(
										searchParams.after,
										1,
										searchParams.intervalType
									),
									searchParams.intervalType
								)
								setSearchParams({
									after,
									before: endOf(after, searchParams.intervalType),
									page: 1,
								})
							}}
						>
							<Icons.ChevronDown rotate="90" />
						</Button>
						<Box>
							<SelectInput
								value={searchParams.intervalType}
								onChange={(e) => {
									const type = (e.currentTarget.value ||
										"month") as TIntervalType
									setSearchParams({
										intervalType: type,
										after: startOf(new Date(), type),
										before: endOf(new Date(), type),
										page: 1,
									})
								}}
							>
								<option value="month">Month</option>
								<option value="week">Week</option>
								<option value="day">Day</option>
							</SelectInput>
						</Box>
						<Button
							onClick={() => {
								const after = startOf(
									addUnit(searchParams.after, 1, searchParams.intervalType),
									searchParams.intervalType
								)
								setSearchParams({
									after,
									before: endOf(after, searchParams.intervalType),
									page: 1,
								})
							}}
						>
							<Icons.ChevronDown rotate="270" />
						</Button>
					</Inline>
				</Inline>
			)}
		>
			<Container paddingY="4">
				<Stack gap="4">
					<Heading as="h5" fontSize="lg">
						{formatDate(params.after, "ddd D MMM")} -{" "}
						{formatDate(params.before, "ddd D MMM")}
					</Heading>
					<Inline paddingY="2" bgColor="default" flexWrap="wrap">
						{stats?.data?.wallet ? (
							<Stack paddingX="6" paddingY="2" gap="2">
								<WalletBalanceCard
									wallet={stats?.data.wallet}
									adminTenantId={
										adminMode && canViewWallet && params.tenant
											? params.tenant.id
											: undefined
									}
								/>
								{stats.data.wallet.approx_remaining_parses != null ? (
									<Text
										fontSize="sm"
										color="muted"
										title="Approx parses the remaining balance can cover at the average cost"
									>
										≈{" "}
										{stats.data.wallet.approx_remaining_parses.toLocaleString()}{" "}
										parses left
									</Text>
								) : null}
							</Stack>
						) : null}
						<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
							<Text color={"muted"}>Total</Text>
							<Text fontSize="xl" fontWeight="semibold">
								{!stats ? "..." : (stats?.data.total ?? 0)}
							</Text>
						</Stack>

						<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
							<Text color={"muted"}>Total Failed</Text>
							<Text fontSize="xl" fontWeight="semibold">
								{!stats ? "..." : (stats?.data.failed ?? 0)}
							</Text>
						</Stack>

						<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
							<Text color={"muted"}>Total Parsed</Text>
							<Text fontSize="xl" fontWeight="semibold">
								{!stats ? "..." : (stats?.data.completed ?? 0)}
							</Text>
						</Stack>

						{stats?.data?.avg_credits_per_parse != null ? (
							<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
								<Text fontWeight="semibold">
									<Icons.BankNotes /> Avg. AI
								</Text>
								<Inline flexWrap="wrap" gap="2" alignItems="baseline">
									<Text
										fontSize="xl"
										fontWeight="semibold"
										title="Average wallet credits debited per parse in this range"
									>
										{Number(stats.data.avg_credits_per_parse).toFixed(2)}
									</Text>
									<Text fontSize="sm" color="muted" whiteSpace="nowrap">
										Credits
									</Text>
								</Inline>
							</Stack>
						) : null}

						{stats?.data?.supplier_cost ? (
							<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
								{adminMode && canViewWallet && params.tenant ? (
									<Link
										to={generatePath(
											"/admin/tenants/:tenantId/subscription-wallet",
											{ tenantId: String(params.tenant.id) }
										)}
									>
										<Text fontWeight="semibold">
											<Icons.BankNotes /> Supplier cost
										</Text>
									</Link>
								) : (
									<Text fontWeight="semibold">
										<Icons.BankNotes /> Supplier cost
									</Text>
								)}
								<Stack gap="1">
									{stats.data.supplier_cost.length === 0 ? (
										<Text color="muted">No vendor cost yet.</Text>
									) : (
										stats.data.supplier_cost.map((row) => (
											<Inline key={row.currency} gap="1" alignItems="baseline">
												<Text as="sup" fontWeight="semibold">
													{row.currency}
												</Text>
												<Text fontSize="lg" fontWeight="semibold">
													{Number(row.total).toFixed(6)}
												</Text>
											</Inline>
										))
									)}
								</Stack>
							</Stack>
						) : null}

						<Stack borderLeftWidth="4" paddingX="6" paddingY="2" gap="2">
							<Text color="muted">Credit Used</Text>
							<Text fontSize="xl" fontWeight="semibold">
								{!stats ? "..." : (stats?.data.credit_used ?? 0)}
							</Text>
						</Stack>
					</Inline>
				</Stack>
			</Container>
			<ListView<TParsingItem, TUsageViewFilters>
				params={params}
				pageKey={adminMode ? "admin-quote-parser" : "quote-parser"}
				fetch={(api) =>
					api
						.get("/trip-quote-parsings", {
							params: requestParams,
						})
						.then((resp) => resp.data)
				}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items }) => {
					return (
						<Table
							headers={[
								"Guest Details",
								"Destination",
								"Status",
								"Credits",
								"Processed",
							]}
							responsive
							bordered
							hover
							rows={items.map((row) => {
								const badge = getParsingBadge(row)
								const tripId = row?.trip?.id || "-"
								const name = row?.trip?.contact?.name || "-"
								const credit = row?.credits_consumed
								return [
									<Link
										color="accent"
										fontWeight="semibold"
										to={generatePath("/trips/:tripId", {
											tripId: tripId.toString(),
										})}
									>
										<Stack>
											<Text>{name}</Text>
											<Text fontSize="sm">{joinAttributes(`# ${tripId}`)}</Text>
										</Stack>
									</Link>,
									<Box minWidth="0">
										<Text>
											{row?.trip?.destinations?.map((d) => d.name).join(", ") ||
												null}
										</Text>
									</Box>,
									badge ? (
										<Badge success={badge.color === "success"}>
											{badge.label}
										</Badge>
									) : (
										<Text fontSize="sm" color="muted">
											—
										</Text>
									),
									credit ? (
										<Text
											fontSize="sm"
											title={`Wallet credits debited for this parse: ${credit}`}
										>
											{Number(credit).toFixed(3)}
										</Text>
									) : (
										<Text fontSize="sm" color="muted">
											—
										</Text>
									),
									row.submitted_at ? (
										<Stack>
											<Text fontSize="sm" color="muted">
												<RelativeTime timestamp={row.submitted_at} />
											</Text>
											{row?.created_by?.name ? (
												<Text
													fontWeight="semibold"
													fontSize={"sm"}
													color={"muted"}
												>
													{row?.created_by?.name}
												</Text>
											) : null}
										</Stack>
									) : (
										<Text fontSize="sm" color="muted">
											-
										</Text>
									),
								]
							})}
						/>
					)
				}}
			</ListView>
		</Search>
	)
}

function Filters() {
	const { user } = useAuthUser()
	const adminMode = !user?.tenant_id
	return (
		<Stack gap="4">
			<SelectInputField name="status" label="Status">
				<option value="">All</option>
				<option value="completed">Parsed</option>
				<option value="failed">Failed</option>
			</SelectInputField>
			{adminMode ? (
				<SelectField
					select={SelectTenants}
					name="tenant"
					label="Tenant"
					multiple={false}
					placeholder="Tenant"
				/>
			) : null}
		</Stack>
	)
}

function filtersToQuery(filters: TUsageViewFilters): TFiltersInQuery {
	const intervalType = filters.intervalType || "month"
	const query: TFiltersInQuery = {
		intervalType,
		after: dateToQuery(filters.after || startOf(new Date(), intervalType)),
		before: dateToQuery(filters.before || endOf(new Date(), intervalType)),
	}
	if (filters.q) query.q = filters.q
	if (filters.page) query.page = filters.page
	if (filters.status) query.status = filters.status
	if (filters.tenant) {
		query.tenant = `${filters.tenant.id}#${filters.tenant.name}`
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TUsageViewFilters {
	const intervalType = query.intervalType || "month"
	const filters: TUsageViewFilters = {
		intervalType,
		after: query.after
			? startOf(parseDateFromQuery(query.after), "day")
			: startOf(new Date(), intervalType),
		before: query.before
			? endOf(parseDateFromQuery(query.before), "day")
			: endOf(new Date(), intervalType),
	}
	if (query.q) filters.q = query.q
	if (query.page) filters.page = query.page
	if (query.status) filters.status = query.status
	if (query.tenant) {
		const [idStr, ...rest] = query.tenant.split("#")
		const id = parseInt(idStr, 10)
		if (Number.isFinite(id)) {
			filters.tenant = { id, name: rest.join("#") }
		}
	}
	return filters
}
